import {Phone, Calendar, Shield, Sparkles, Car, Award, Check, Zap, MapPin} from 'lucide-react'
import { useNavigate } from "react-router-dom"
import { useEffect } from "react"
import CanonicalTag from '@/components/CanonicalTag'

const PpfDelMarPage = () => {
  const navigate = useNavigate()

  useEffect(() => {
    const script = document.createElement('script')
    script.type = 'application/ld+json'
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "How much does paint protection film cost in Del Mar?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Partial front PPF starts at $1,299, full front starts at $1,899, and full vehicle coverage starts at $4,499. Final pricing depends on vehicle type, paint condition, and coverage selection."
          }
        },
        {
          "@type": "Question",
          "name": "Does PPF protect against Del Mar's ocean air and salt damage?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes. PPF creates a barrier against salt corrosion, ocean spray, and marine layer moisture that accelerates paint oxidation in Del Mar's coastal environment. Film prevents clear coat degradation and maintains factory finish."
          }
        },
        {
          "@type": "Question",
          "name": "How long does PPF installation take?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Partial front coverage takes 1 day, full front requires 2 days, and full vehicle installation takes 3-5 days depending on vehicle complexity and coverage areas."
          }
        },
        {
          "@type": "Question",
          "name": "Can I still use Del Mar car washes after PPF installation?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes. After 7-day curing period, you can safely use touchless car washes or hand washing. PPF's hydrophobic coating actually makes washing easier and helps maintain a cleaner finish longer."
          }
        }
      ]
    })
    document.head.appendChild(script)

    document.title = "Paint Protection Film Del Mar | Professional PPF Installation for Luxury Vehicles"
    const metaDescription = document.querySelector('meta[name="description"]')
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Expert paint protection film installation in Del Mar, CA. Protect luxury vehicles with professional PPF services. Self-healing technology, manufacturer-backed film warranties. Serving Del Mar Heights, Carmel Valley, and coastal North County.')
    }

    return () => {
      document.head.removeChild(script)
    }
  }, [])

  return (
    <>
      <CanonicalTag url="https://autowrapsandiego.com/ppf-del-mar" />
      <div className="pt-20 min-h-screen bg-black">
      {/* Hero */}
      <section className="bg-gradient-to-br from-black via-gray-900 to-black text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-5xl font-bold mb-6">Paint Protection Film in Del Mar, CA</h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-4">
              Protect your vehicle's paint with professional paint protection film installation serving Del Mar, California. Our installers provide premium PPF coverage for Tesla, Porsche, BMW, Mercedes, and luxury vehicles—delivering invisible protection against coastal salt air, highway debris, and environmental damage common in Del Mar's oceanfront community.
            </p>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
              Serving Del Mar Heights, Carmel Valley, Torrey Hills, and Torrey Pines residents with bulk or precision-pattern installation, self-healing technology, and manufacturer-backed film warranties. Conveniently located in Vista, just 20 minutes from Del Mar Plaza and the Del Mar Fairgrounds via I-5, we offer same-week appointments for discerning vehicle owners who demand perfection.
            </p>
            <div className="flex gap-4 justify-center flex-wrap">
              <a href="tel:858-282-8119" className="flex items-center gap-2 bg-cyan-500 hover:bg-cyan-600 text-white px-6 py-3 rounded-lg font-semibold transition">
                <Phone size={18} />
                858-282-8119
              </a>
              <button onClick={() => { navigate("/contact"); setTimeout(() => window.scrollTo(0, 0), 100); }} className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-lg font-semibold transition">
                <Calendar size={18} />
                Get Free PPF Quote
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Luxury Vehicle Protection */}
      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Luxury Vehicle Paint Protection in Del Mar</h2>
          <div className="max-w-4xl mx-auto space-y-6 text-gray-300 text-lg">
            <p>
              Del Mar's affluent oceanfront community drives some of North County San Diego's finest automobiles. We specialize in professional PPF installation for Porsche 911 Carrera, Turbo, GT3, Cayenne, and Macan models driven throughout Del Mar Heights and Carmel Valley. BMW M-series, X5, X7, and luxury sedan owners trust our installers for precision coverage that protects German engineering excellence from Del Mar's aggressive coastal environment.
            </p>
            <p>
              Mercedes-Benz AMG vehicles, G-Wagon SUVs, S-Class, and E-Class sedans receive expert paint protection at our Vista facility. <a href="/tesla-ppf-san-diego" className="text-cyan-400 hover:underline">Tesla PPF installation</a> protects the substantial Model S, Model X, Model 3, and Model Y population along Camino Del Mar. Audi RS models, Q7, Q8, and e-tron vehicles benefit from our experience with complex European paint systems and manufacturer-specific installation requirements.
            </p>
            <p>
              Range Rover, Bentley Continental GT, Maserati, and exotic supercars driven in Del Mar receive white-glove service worthy of their exceptional valuations. Our <a href="/paint-protection-film" className="text-cyan-400 hover:underline">paint protection film expertise</a> extends to limited-production vehicles, custom paint finishes, and collector automobiles where paint preservation is critical for maintaining investment value in North County San Diego's competitive luxury automotive market.
            </p>
          </div>
        </div>
      </section>

      {/* Why Del Mar Needs PPF */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Why Del Mar Vehicle Owners Need Paint Protection Film</h2>
          <div className="max-w-4xl mx-auto space-y-6 text-gray-300 text-lg">
            <p>
              Del Mar's direct oceanfront location creates one of Southern California's most challenging environments for automotive paint protection. Constant salt air from the Pacific Ocean, marine layer moisture every morning, and intense coastal UV exposure cause accelerated paint oxidation, clear coat failure, and corrosion. <a href="/paint-protection-film" className="text-cyan-400 hover:underline">Paint protection film</a> provides essential defense for vehicles in this premium coastal community where six-figure automobiles are standard and paint preservation is critical for resale value.
            </p>
            <p>
              Vehicle owners along Camino Del Mar, Del Mar Heights Road, and throughout Carmel Valley drive luxury automobiles that represent significant investments—Tesla Model S and Model X, Porsche 911 and Cayenne, BMW X5 and M-series, Mercedes G-Wagon and AMG variants, Range Rover, and exotic supercars. These high-end vehicles require professional protection against environmental damage that occurs daily in Del Mar's oceanfront microclimate. PPF installation prevents the paint degradation, water spotting, and oxidation that plague unprotected vehicles in coastal areas.
            </p>
            <p>
              Del Mar's proximity to I-5 means highway rock chips, gravel spray, and debris impacts from construction vehicles traveling between San Diego and Oceanside. Commuting to Sorrento Valley, UTC, or downtown San Diego exposes front-facing surfaces to constant projectile damage that creates permanent paint chips and divots. Professional PPF installation absorbs these impacts, protecting factory paint and maintaining your vehicle's flawless appearance despite daily highway driving.
            </p>
            <p>
              Beach parking at Del Mar Beach, Powerhouse Park, and Seagrove Park subjects vehicles to door dings, shopping cart scratches, and careless pedestrian contact. During Del Mar Racing Season and Fair season, parking lot congestion multiplies impact risks. Self-healing PPF technology repairs light scratches and swirl marks with sun heat, ideal for Del Mar's beach parking environments where minor contact damage is inevitable.
            </p>
            <p>
              Our <a href="/tesla-ppf-san-diego" className="text-cyan-400 hover:underline">Tesla PPF installation services</a> are particularly popular with Del Mar's substantial Tesla owner community. Tesla's thin, water-based paint is exceptionally vulnerable to rock chips and environmental etching. We install model-specific PPF patterns on dozens of Del Mar Tesla vehicles monthly, protecting Model 3, Model Y, Model S, Model X, and Cybertruck with precision coverage that maintains Autopilot sensor functionality and factory warranty compliance.
            </p>
          </div>
        </div>
      </section>

      {/* Coverage Options */}
      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">PPF Coverage Packages for Del Mar Vehicles</h2>
          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <div className="bg-black p-8 rounded-xl border border-cyan-500/20">
              <h3 className="text-2xl font-bold mb-4 text-white">Partial Front Coverage</h3>
              <p className="text-gray-300 mb-6">
                Essential protection for high-impact zones: front bumper, partial hood, mirrors, and headlights. Ideal for daily commuters and highway drivers.
              </p>
              <div className="text-cyan-400 text-3xl font-bold mb-2">Starting at $1,299</div>
              <p className="text-gray-400 text-sm mb-6">Pricing varies by vehicle</p>
              <ul className="space-y-2 text-gray-300">
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Front bumper full coverage</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Partial hood (12-18 inches)</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Mirrors and headlights</span>
                </li>
              </ul>
            </div>
            <div className="bg-black p-8 rounded-xl border-2 border-cyan-500">
              <div className="bg-cyan-500 text-black text-xs font-bold px-3 py-1 rounded-full inline-block mb-4">MOST POPULAR</div>
              <h3 className="text-2xl font-bold mb-4 text-white">Full Front Coverage</h3>
              <p className="text-gray-300 mb-6">
                Comprehensive front-end protection: full hood, bumper, fenders, mirrors, headlights. Maximum protection for Del Mar coastal drivers.
              </p>
              <div className="text-cyan-400 text-3xl font-bold mb-2">Starting at $1,899</div>
              <p className="text-gray-400 text-sm mb-6">Pricing varies by vehicle</p>
              <ul className="space-y-2 text-gray-300">
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Full hood, fenders, and bumper</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Headlights, fog lights, mirrors</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Manufacturer-backed film warranty</span>
                </li>
              </ul>
            </div>
            <div className="bg-black p-8 rounded-xl border border-cyan-500/20">
              <h3 className="text-2xl font-bold mb-4 text-white">Full Vehicle Coverage</h3>
              <p className="text-gray-300 mb-6">
                Ultimate protection for every painted surface. Maintain showroom condition and maximize resale value for luxury vehicles.
              </p>
              <div className="text-cyan-400 text-3xl font-bold mb-2">Starting at $4,499</div>
              <p className="text-gray-400 text-sm mb-6">Pricing varies by vehicle</p>
              <ul className="space-y-2 text-gray-300">
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>All full front coverage areas</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Doors, rocker panels, quarters</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Roof, trunk, rear bumper</span>
                </li>
              </ul>
            </div>
          </div>
          <p className="text-center text-gray-400 text-sm">
            Starting prices shown are for standard vehicles. Exact pricing will be determined after a free consultation and vehicle inspection at our Vista facility.
          </p>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Premium PPF Installation for Del Mar's Luxury Vehicles</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 border border-cyan-500">
                <Shield className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">Coastal Defense</h3>
              <p className="text-gray-300">Advanced protection against Del Mar's salt air, ocean spray, and UV damage that destroys unprotected automotive paint.</p>
            </div>
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 border border-cyan-500">
                <Sparkles className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">Self-Healing Technology</h3>
              <p className="text-gray-300">Film repairs minor scratches, swirl marks, and light damage with heat—perfect for Del Mar beach parking environments.</p>
            </div>
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 border border-cyan-500">
                <Car className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">Luxury Expertise</h3>
              <p className="text-gray-300">Professional installation for Porsche, Tesla, BMW, Mercedes, and exotic vehicles common in Del Mar's affluent community.</p>
            </div>
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 border border-cyan-500">
                <Award className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">Manufacturer-Backed Warranty</h3>
              <p className="text-gray-300">Manufacturer warranty against yellowing, cracking, peeling, plus workmanship guarantee on installation quality.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Tesla PPF Section */}
      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Tesla Paint Protection Film for Del Mar Owners</h2>
          <div className="max-w-4xl mx-auto space-y-6 text-gray-300 text-lg mb-12">
            <p>
              Del Mar has one of California's highest concentrations of Tesla ownership, with Model 3, Model Y, Model S, and Model X vehicles filling driveways along Camino Del Mar and throughout Del Mar Heights. Tesla's water-based paint requires specialized protection against Del Mar's aggressive coastal environment. <a href="/tesla-ppf-san-diego" className="text-cyan-400 hover:underline">Professional Tesla PPF installation</a> preserves factory finish and prevents the rock chips, environmental etching, and paint degradation that plague unprotected Teslas in oceanfront communities.
            </p>
            <p>
              We install model-specific PPF patterns designed for every Tesla variant—Model 3 Standard Range and Performance, Model Y Long Range and Performance, Model S Plaid, Model X, and Cybertruck. Bulk or precision-pattern methods keep coverage complete across complex body lines, door handles, and charging port areas without unnecessary cutting on the vehicle. Our installation process protects all Autopilot sensors, cameras, and radar units while delivering seamless edge-to-edge coverage.
            </p>
            <p>
              Most Del Mar Tesla owners choose full front coverage (starting at $1,899) for comprehensive highway protection, or full vehicle coverage (starting at $4,499) for maximum resale value preservation. Many schedule installation immediately after delivery from Tesla's San Diego delivery center to protect factory paint from day one. We also offer <a href="/chrome-delete-san-diego" className="text-cyan-400 hover:underline">chrome delete</a> and <a href="/window-tint-san-diego" className="text-cyan-400 hover:underline">ceramic window tint</a> services that complement Tesla PPF for complete protection and customization.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-black p-8 rounded-xl border border-cyan-500/20">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 border border-cyan-500">
                <Zap className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">Model-Specific Templates</h3>
              <p className="text-gray-300">Precision-cut patterns for every Tesla model ensure flawless fitment without blade work on your paint.</p>
            </div>
            <div className="bg-black p-8 rounded-xl border border-cyan-500/20">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 border border-cyan-500">
                <Shield className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">Sensor Protection</h3>
              <p className="text-gray-300">Proper masking and installation around Autopilot hardware ensures film doesn't interfere with Tesla technology.</p>
            </div>
            <div className="bg-black p-8 rounded-xl border border-cyan-500/20">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 border border-cyan-500">
                <Award className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">Factory Warranty Safe</h3>
              <p className="text-gray-300">Professional PPF installation doesn't void Tesla warranty—it helps maintain warranty-compliant paint condition.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Installation Process */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Our Professional PPF Installation Process</h2>
          <div className="max-w-4xl mx-auto space-y-8">
            <div className="flex gap-6">
              <div className="bg-cyan-500 text-black font-bold w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 text-xl">1</div>
              <div>
                <h3 className="text-2xl font-bold mb-3 text-white">Free Consultation & Paint Inspection</h3>
                <p className="text-gray-300">We inspect your vehicle's paint condition, discuss Del Mar-specific protection needs, and provide a detailed quote. For luxury and exotic vehicles, we review manufacturer paint specifications and recommend appropriate coverage to protect your investment.</p>
              </div>
            </div>
            <div className="flex gap-6">
              <div className="bg-cyan-500 text-black font-bold w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 text-xl">2</div>
              <div>
                <h3 className="text-2xl font-bold mb-3 text-white">Paint Preparation & Decontamination</h3>
                <p className="text-gray-300">Thorough wash, clay bar treatment, and paint decontamination remove embedded particles, salt residue, and surface contaminants. For used vehicles, we perform multi-stage paint correction to eliminate swirl marks, water spots, and oxidation before PPF installation, ensuring a flawless finished appearance.</p>
              </div>
            </div>
            <div className="flex gap-6">
              <div className="bg-cyan-500 text-black font-bold w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 text-xl">3</div>
              <div>
                <h3 className="text-2xl font-bold mb-3 text-white">Precision Installation, Selected by Vehicle and Panel</h3>
                <p className="text-gray-300">Using vehicle-specific templates, we apply premium self-healing PPF with professional wet installation techniques. Proper squeegee methods, edge wrapping, and heat forming achieve seamless coverage across complex curves and body lines. Installation takes place in our indoor facility to support adhesion and clean, bubble-free results.</p>
              </div>
            </div>
            <div className="flex gap-6">
              <div className="bg-cyan-500 text-black font-bold w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 text-xl">4</div>
              <div>
                <h3 className="text-2xl font-bold mb-3 text-white">Quality Inspection & Delivery</h3>
                <p className="text-gray-300">Final multi-point inspection verifies perfect edges, complete adhesion, and optical clarity. We provide comprehensive care instructions, warranty documentation, and long-term maintenance recommendations. Your vehicle returns to Del Mar with invisible protection and showroom finish.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Local Context */}
      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Paint Protection for Del Mar's Oceanfront Community</h2>
          <div className="max-w-4xl mx-auto space-y-6 text-gray-300 text-lg">
            <p>
              Del Mar's oceanfront location means vehicles face relentless environmental assault. Salt particles from Pacific Ocean waves travel inland on coastal winds, settling on paint surfaces where they attract moisture and accelerate oxidation. Morning marine layer deposits corrosive salt film that etches into clear coat if not removed promptly. Afternoon sun bakes these contaminants deeper into paint, causing permanent staining and degradation visible within months on unprotected vehicles.
            </p>
            <p>
              Del Mar Plaza, Del Mar Highlands Town Center, and One Paseo shopping destinations subject vehicles to parking lot damage risks. Crowded parking during racing season, fair season, and summer beach weekends increases door ding probability. PPF absorbs these impacts, preventing paint chips and dents that would otherwise require expensive body shop repair. Self-healing technology repairs light scratches from shopping carts and careless door openings common in congested Del Mar parking environments.
            </p>
            <p>
              Highway commuting from Del Mar to Sorrento Valley, La Jolla, or downtown San Diego via I-5 exposes vehicles to constant rock chip bombardment. Construction debris, gravel spray from trucks, and projectiles from traffic create paint damage that worsens over time. A single unprotected highway drive can result in dozens of micro-chips across your hood and bumper. PPF installation prevents this cumulative damage, maintaining factory paint integrity for years of Del Mar coastal driving.
            </p>
            <p>
              Beach parking at Powerhouse Park, Seagrove Park, and Del Mar Beach subjects vehicles to sand abrasion, seagull droppings, and saltwater overspray. Vehicles parked near Del Mar's oceanfront endure harsher conditions than inland areas. Bird droppings contain uric acid that permanently etches paint within hours if not removed. PPF's smooth surface prevents adhesion and allows safe contaminant removal without paint damage, essential for maintaining pristine condition in Del Mar's coastal environment.
            </p>
            <p>
              Del Mar Heights and Carmel Valley residents driving luxury vehicles understand that paint protection is not optional—it's essential maintenance for preserving six-figure automotive investments. Professional PPF installation delivers long-term value by preventing the paint degradation, environmental damage, and oxidation that plague coastal vehicles. When it's time to sell or trade, PPF-protected vehicles command premium prices due to maintained factory paint condition and documented professional care.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Del Mar PPF FAQs</h2>
          <div className="space-y-6">
            <div className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">How much does paint protection film cost in Del Mar?</h3>
              <p className="text-gray-300">Partial front PPF starts at $1,299, full front starts at $1,899, and full vehicle coverage starts at $4,499. Final pricing depends on vehicle type, paint condition, and coverage selection. We provide transparent quotes for all Del Mar customers with detailed breakdowns and no hidden fees.</p>
            </div>
            <div className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">Does PPF protect against Del Mar's ocean air and salt damage?</h3>
              <p className="text-gray-300">Yes. PPF creates a waterproof, non-porous barrier against salt corrosion, ocean spray, and marine layer moisture that accelerates paint oxidation in Del Mar's coastal environment. The film prevents clear coat degradation and maintains factory finish despite constant salt air exposure.</p>
            </div>
            <div className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">How long does PPF installation take?</h3>
              <p className="text-gray-300">Partial front coverage takes 1 day, full front requires 2 days, and full vehicle installation takes 3-5 days depending on vehicle complexity and coverage areas. We never rush quality work—proper curing time ensures long-lasting adhesion and bubble-free results worthy of Del Mar's luxury vehicle standards.</p>
            </div>
            <div className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">Can I still use Del Mar car washes after PPF installation?</h3>
              <p className="text-gray-300">Yes. After the 7-day curing period, you can safely use touchless car washes or hand washing with pH-neutral soap. PPF's hydrophobic coating actually makes washing easier and helps maintain a cleaner finish longer in Del Mar's coastal environment. Avoid automatic brush washes that can cause edge lifting.</p>
            </div>
            <div className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">Will PPF damage my vehicle's paint when removed?</h3>
              <p className="text-gray-300">No. When professionally installed and removed, PPF leaves no adhesive residue or paint damage. In fact, removing PPF after 10+ years reveals factory-fresh paint underneath while surrounding unprotected areas show significant wear, UV fading, and environmental damage.</p>
            </div>
            <div className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">Does PPF affect vehicle appearance or color?</h3>
              <p className="text-gray-300">Premium PPF is optically clear and invisible when properly installed. Most Del Mar customers cannot see or feel the film after installation. The hydrophobic top coat actually enhances paint depth and gloss, making colors appear richer and more vibrant while providing superior protection.</p>
            </div>
            <div className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">Can I combine PPF with other services?</h3>
              <p className="text-gray-300">Absolutely. Many Del Mar customers combine PPF with ceramic window tint for UV protection, chrome delete for modern aesthetics, and caliper painting for complete customization. Package pricing available for combined services scheduled in one visit.</p>
            </div>
            <div className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">Is PPF worth it for lease vehicles in Del Mar?</h3>
              <p className="text-gray-300">Yes. PPF prevents excess wear charges at lease return by maintaining factory paint condition. The cost of PPF installation is typically less than paint repair fees charged by leasing companies for rock chips, scratches, and environmental damage accumulated during a 3-year lease term in Del Mar's harsh coastal climate.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Related Services */}
      <section className="py-16 bg-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold mb-8 text-white text-center">Related Services</h2>
          <p className="text-gray-300 text-center mb-8">
            Comprehensive vehicle protection and customization for Del Mar's luxury automotive community:
          </p>
          <div className="grid md:grid-cols-3 gap-6">
            <a href="/paint-protection-film-san-diego" className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20 hover:border-cyan-500 transition group">
              <h3 className="text-xl font-bold text-white mb-2 group-hover:text-cyan-400 transition">Paint Protection Film San Diego</h3>
              <p className="text-gray-400 text-sm">Full PPF pricing, coverage options, and installation guide for San Diego</p>
            </a>
            <a href="/tesla-ppf-san-diego" className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20 hover:border-cyan-500 transition group">
              <h3 className="text-xl font-bold text-white mb-2 group-hover:text-cyan-400 transition">Tesla PPF San Diego</h3>
              <p className="text-gray-400 text-sm">Specialized PPF for all Tesla models with precision installation</p>
            </a>
            <a href="/ppf-san-diego" className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20 hover:border-cyan-500 transition group">
              <h3 className="text-xl font-bold text-white mb-2 group-hover:text-cyan-400 transition">PPF San Diego</h3>
              <p className="text-gray-400 text-sm">San Diego paint protection film services for all vehicles</p>
            </a>
            <a href="/window-tint-san-diego" className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20 hover:border-cyan-500 transition group">
              <h3 className="text-xl font-bold text-white mb-2 group-hover:text-cyan-400 transition">Ceramic Window Tint</h3>
              <p className="text-gray-400 text-sm">UV protection and heat rejection for Del Mar's intense coastal sun</p>
            </a>
          </div>
        </div>
      </section>

      {/* Service Area */}
      <section className="py-16 bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold mb-8 text-white text-center flex items-center justify-center gap-3">
            <MapPin className="text-cyan-400" size={32} />
            Serving Del Mar & North County Coastal Communities
          </h2>
          <p className="text-gray-300 text-lg mb-6 text-center">
            Located in Vista, CA, just 20 minutes from Del Mar via I-5. We serve luxury vehicle owners throughout Del Mar and coastal San Diego with premium PPF installation.
          </p>
          <div className="grid md:grid-cols-2 gap-4 text-gray-300">
            <div className="bg-black p-4 rounded-lg border border-cyan-500/20">
              <h3 className="font-bold text-white mb-2">Del Mar Areas</h3>
              <ul className="space-y-1 text-sm">
                <li>• Del Mar Village</li>
                <li>• Del Mar Heights</li>
                <li>• Torrey Hills</li>
                <li>• Carmel Valley</li>
                <li>• Torrey Pines</li>
                <li>• Del Mar Mesa</li>
              </ul>
            </div>
            <div className="bg-black p-4 rounded-lg border border-cyan-500/20">
              <h3 className="font-bold text-white mb-2">Nearby Coastal Cities</h3>
              <ul className="space-y-1 text-sm">
                <li>• <a href="/ppf-solana-beach" className="text-cyan-400 hover:underline">Solana Beach</a></li>
                <li>• <a href="/ppf-encinitas" className="text-cyan-400 hover:underline">Encinitas</a></li>
                <li>• <a href="/ppf-carlsbad" className="text-cyan-400 hover:underline">Carlsbad</a></li>
                <li>• <a href="/ppf-la-jolla" className="text-cyan-400 hover:underline">La Jolla</a></li>
                <li>• <a href="/ppf-rancho-santa-fe" className="text-cyan-400 hover:underline">Rancho Santa Fe</a></li>
              </ul>
            </div>
          </div>
          <p className="text-gray-300 text-center mt-8">
            <strong className="text-white">Shop Address:</strong> 2244 South Santa Fe Avenue, Suite A7, Vista, CA 92084
          </p>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-r from-cyan-500 to-cyan-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6 text-white">Protect Your Del Mar Vehicle with Professional PPF</h2>
          <p className="text-xl text-white/90 mb-8">
            Schedule your free consultation and receive a custom quote for paint protection film installation. Same-week appointments available for Del Mar residents.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button onClick={() => { navigate("/contact"); setTimeout(() => window.scrollTo(0, 0), 100); }} className="bg-orange-500 hover:bg-orange-600 text-white font-bold py-4 px-12 rounded-lg transition-all transform hover:scale-105 shadow-xl inline-flex items-center justify-center gap-2">
              <Calendar size={20} />
              Get Free PPF Quote
            </button>
            <a href="tel:858-282-8119" className="bg-white hover:bg-gray-100 text-gray-900 font-bold py-4 px-12 rounded-lg transition-all transform hover:scale-105 shadow-xl inline-flex items-center justify-center gap-2">
              <Phone size={20} />
              Call: 858-282-8119
            </a>
          </div>
        </div>
      </section>
      </div>
    </>
  )
}

export default PpfDelMarPage
